import { useEffect, useState } from "react";
import { Card, CardContent } from "@mui/material";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { DeviationsService } from "../../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService";
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable";
import { MDBox } from "../../../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../../../Component/Common/Components/MDTypography";
import { Deviations } from "../../../../../Component/Model/Dto/EmploymentEquity/Deviations";
import { SearchResult } from "../../../../../Component/Model/Dto/SearchResult";
import { ColumnSortOption } from "../../../../../Component/Model/Dto/ColumnSortOption";
import { IBulkActions } from "../../../../../Component/Model/Table/Table";
import { EEOccupationalLevel } from "../../../../../Component/Model/Enum/EmploymentEquity";
import { ScorecardAtom } from "../../../../../State/Scorecard.Atom";
import colors from "../../../../../assets/theme/base/colors";
import { DeviationModal } from "../../../Plan/WorkforceProfile/Deviations/DeviationModal";
import { PortalDocuments } from "../../../../Common/PortalDocuments/PortalDocuments";
import { OccupationalLevelJustificationsTableMapper } from "./OccupationalLevelJustificationsTableColumn";

interface IProps {
    occupationalLevelType: number;
    isOpen: boolean;
    isDisabled?: boolean;
    disableCard?: boolean;
    onJustificationsChanged?: () => void;
}

export const OccupationalLevelJustificationsTable = (props: IProps) => {
    const scorecard = useRecoilValue(ScorecardAtom);
    const scorecardId = scorecard.scorecardId;

    const [data, setData] = useState<SearchResult<Deviations.SearchResultDto> | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState<boolean>(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const request: Deviations.SearchRequest = {
                pageNumber: 1,
                pageSize: 100,
                searchValue: "",
                columnSortOptions,
                scorecardId,
                occupationalLevelType: props.occupationalLevelType,
                isDisabled: props.isDisabled ?? false,
                justificationType: 1,
            };
            const response = await DeviationsService.SearchJustifications(request);
            setData(response.data);
        } catch {
            toast.error("Failed to load justifications");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!props.isOpen) return;
        fetchData();
    }, [columnSortOptions, props.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
    };

    const handleAdd = () => {
        setSelectedId(undefined);
        setShowModal(true);
    };

    const handleEdit = (id: number) => {
        setSelectedId(id);
        setShowModal(true);
    };

    const handleConfirmDelete = (id: number) => {
        setSelectedId(id);
        setShowDeleteDialog(true);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await DeviationsService.Delete(scorecardId, selectedId!);
            toast.success("Deviation deleted successfully");
            fetchData();
            props.onJustificationsChanged?.();
        } catch {
            toast.error("Failed to delete deviation");
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setSelectedId(undefined);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedId(undefined);
        fetchData();
        props.onJustificationsChanged?.();
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setSelectedId(undefined);
    };

    const handleDocuments = (id: number) => {
        setSelectedId(id);
        setShowDocumentsModal(true);
    };

    const handleCloseDocuments = () => {
        setShowDocumentsModal(false);
        setSelectedId(undefined);
        fetchData();
        props.onJustificationsChanged?.();
    };

    const bulkActions: IBulkActions[] = [
        { label: "ADD JUSTIFICATION", action: handleAdd },
    ];

    const tableData = OccupationalLevelJustificationsTableMapper(data?.results || [], handleEdit, handleConfirmDelete, handleDocuments);

    return (
        <>
            <Card sx={{
                borderRadius: props.disableCard ? 0 : '10px',
                boxShadow: 'none',
                border: props.disableCard ? 'none' : `0.8px solid ${colors.ui.border}`,
                ...(!props.disableCard && {
                    '& .MuiPaper-root': {
                        boxShadow: 'none',
                        border: `0.8px solid ${colors.ui.border}`,
                    },
                }),
                '& .MuiButton-outlinedPrimary': {
                    borderColor: colors.ui.activeBackground,
                    color: colors.ui.activeBackground,
                    borderWidth: '0.8px',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: '6px 12px',
                    '&:hover': {
                        borderColor: colors.ui.activeBackgroundHover,
                        color: colors.ui.activeBackgroundHover,
                        backgroundColor: 'transparent',
                    },
                },
            }}>
                <CardContent sx={props.disableCard ? { p: 0, '&:last-child': { pb: 0 } } : { p: 3, '&:last-child': { pb: 3 } }}>
                    <MDBox mb={0.5}>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="uppercase" color="dark">
                            {props.isDisabled ? 'Justifications' : 'Occupational level justifications'}
                        </MDTypography>
                    </MDBox>
                    <MDBox mb={2}>
                        <MDTypography variant="caption" color="secondary" style={{ lineHeight: 1.4, display: 'block' }}>
                            Please capture key justification reasons where your organisation did not achieve any of the numerical targets for the current year. Note that only one justification per occupational level can be included in the generated EEA2 report - mark that justification as the EEA2 Key Justifiable Reason.
                        </MDTypography>
                    </MDBox>
                    <DataTable
                        table={tableData}
                        isLoading={isLoading}
                        manualSorting={true}
                        columnSortingChanged={columnSortingChanged}
                        showTotalEntries={false}
                        showEntriesPerPage={false}
                        showBottomMargin={false}
                        isButtonOutlined={true}
                        bulkActions={bulkActions}
                        noDataMessage="No justifications captured yet"
                    />
                </CardContent>
            </Card>

            {showModal && (
                <DeviationModal
                    showModal={showModal}
                    id={selectedId}
                    scorecardId={scorecardId}
                    handleCancel={handleCloseModal}
                    occupationalLevel={props.occupationalLevelType as EEOccupationalLevel}
                    isDisabled={props.isDisabled ?? false}
                    titleContext={props.isDisabled ? 'Disabled Employees' : undefined}
                />
            )}

            <ConfirmationDialog
                showDialog={showDeleteDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseDeleteDialog}
                heading="Delete Justification"
                message="Are you sure you want to delete this justification and associated files?"
                isLoading={isDeleting}
            />

            {showDocumentsModal && (
                <PortalDocuments
                    display={showDocumentsModal}
                    handleCancel={handleCloseDocuments}
                    link="deviation"
                    id={selectedId!}
                />
            )}
        </>
    );
};
